import React, { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Toast from '../components/Toast';
import MapComponent from '../components/MapComponent';
import Breadcrumbs from '../components/Breadcrumbs';
import { normalizePhone, normalizeEmail, validateLatLng } from '../utils/validationUtils';

const ReportCamp = () => {
  const [description, setDescription] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [availability, setAvailability] = useState(null);

  // Listen to availability status
  React.useEffect(() => {
    const unsubAvail = onSnapshot(doc(db, 'availability', 'settings'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        let showBanner = true;
        if (data.expirationDate) {
          const expireTime = data.expirationDate.toDate ? data.expirationDate.toDate() : new Date(data.expirationDate);
          if (new Date() > expireTime) {
            showBanner = false;
          }
        }
        setAvailability({
          ...data,
          showBanner: showBanner && data.status !== 'normal'
        });
      } else {
        setAvailability(null);
      }
    });

    return () => unsubAvail();
  }, []);

  // Observations
  const [peopleCount, setPeopleCount] = useState('');
  const [safetyConcern, setSafetyConcern] = useState(false);
  const [childrenObserved, setChildrenObserved] = useState('unknown');
  const [petsObserved, setPetsObserved] = useState('unknown');
  const [observedNeeds, setObservedNeeds] = useState('');
  const [accessibilityConcerns, setAccessibilityConcerns] = useState('');
  const [spokeWithAnyone, setSpokeWithAnyone] = useState(false);
  const [dateObserved, setDateObserved] = useState('');
  const [timeObserved, setTimeObserved] = useState('');

  // Reporter details
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [permissionToContact, setPermissionToContact] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const submittedRef = useRef(false);
  const navigate = useNavigate();

  const handleLocationSelect = (lat, lng) => {
    setLatitude(lat);
    setLongitude(lng);
  };

  const [fieldErrors, setFieldErrors] = useState({});

  const DRAFT_KEY = 'tentfinder_reportcamp_draft';

  React.useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const d = JSON.parse(saved);
        if (d.description) setDescription(d.description);
        if (d.latitude) setLatitude(d.latitude);
        if (d.longitude) setLongitude(d.longitude);
        if (d.peopleCount) setPeopleCount(d.peopleCount);
        if (d.safetyConcern !== undefined) setSafetyConcern(d.safetyConcern);
        if (d.observedNeeds) setObservedNeeds(d.observedNeeds);
        if (d.accessibilityConcerns) setAccessibilityConcerns(d.accessibilityConcerns);
        if (d.email) setEmail(d.email);
        if (d.phone) setPhone(d.phone);
        if (d.additionalInfo) setAdditionalInfo(d.additionalInfo);
      }
    } catch (e) {
      console.warn('Could not restore ReportCamp draft:', e);
    }
  }, []);

  React.useEffect(() => {
    if (submittedRef.current) return;
    try {
      const draft = {
        description, latitude, longitude, peopleCount, safetyConcern,
        observedNeeds, accessibilityConcerns, email, phone, additionalInfo
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch (e) {}
  }, [description, latitude, longitude, peopleCount, safetyConcern, observedNeeds, accessibilityConcerns, email, phone, additionalInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting || submittedRef.current) return;

    const errors = {};
    if (!description.trim()) {
      errors.description = 'Please provide a description of the camp location.';
    }
    if (!validateLatLng(latitude, longitude)) {
      errors.coords = 'Coordinates must both be empty or a valid latitude (-90 to 90) and longitude (-180 to 180).';
    }
    if (email.trim() && !email.includes('@')) {
      errors.email = 'Please enter a valid email address.';
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErr = Object.values(errors)[0];
      setToast({ message: firstErr, type: 'error' });
      return;
    }

    setSubmitting(true);
    submittedRef.current = true;

    const cleanEmail = normalizeEmail(email);
    const cleanPhone = normalizePhone(phone);

    try {
      await addDoc(collection(db, 'camp_reports'), {
        description: description.trim(),
        latitude: latitude,
        longitude: longitude,
        peopleCount: peopleCount ? parseInt(peopleCount, 10) || null : null,
        safetyConcern: !!safetyConcern,
        childrenObserved: childrenObserved,
        petsObserved: petsObserved,
        observedNeeds: observedNeeds.trim() || null,
        accessibilityConcerns: accessibilityConcerns.trim() || null,
        spokeWithAnyone: !!spokeWithAnyone,
        dateObserved: dateObserved || null,
        timeObserved: timeObserved || null,
        email: cleanEmail || null,
        phone: cleanPhone || null,
        permissionToContact: !!permissionToContact,
        additionalInfo: additionalInfo.trim() || null,
        active: true,
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        schemaVersion: 2,
        notificationStatus: 'pending'
      });

      setToast({
        message: 'Thank you! Your report has been submitted. Information is shared only with authorized outreach volunteers.',
        type: 'success',
      });

      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {}
      setDescription('');
      setPeopleCount('');
      setSafetyConcern(false);
      setChildrenObserved('unknown');
      setPetsObserved('unknown');
      setObservedNeeds('');
      setAccessibilityConcerns('');
      setSpokeWithAnyone(false);
      setDateObserved('');
      setTimeObserved('');
      setEmail('');
      setPhone('');
      setPermissionToContact(false);
      setAdditionalInfo('');
      setLatitude(null);
      setLongitude(null);

      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error submitting camp report:', error);
      submittedRef.current = false;
      setToast({ message: 'Error submitting your report. Please try again.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const getInputClass = (hasError) =>
    `w-full px-4 py-2.5 bg-charcoal-card border ${
      hasError ? 'border-urgent-orange ring-1 ring-urgent-orange' : 'border-white/10'
    } rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-accent-cyan focus:border-transparent`;

  const inputClass = getInputClass(false);

  return (
    <div className="min-h-screen flex flex-col bg-charcoal-dark text-slate-200">
      <Header />
      <Breadcrumbs />

      <main id="main-content" className="flex-grow max-w-2xl mx-auto w-full px-4 md:px-8 py-8">
        <div className="chrome-panel border border-white/5 rounded-lg p-6 md:p-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 uppercase tracking-tight">
            Report Camp Location
          </h2>

          <div className="mb-6 rounded-lg border border-accent-cyan/40 bg-accent-cyan/10 px-4 py-3 text-xs leading-relaxed" role="note">
            <p className="text-white font-bold uppercase tracking-wide text-2xs mb-1">
              Instructions for reporting:
            </p>
            <p className="text-slate-300">
              Please report only what you reasonably observed. Do not enter unnecessary names, photographs, medical information, or other identifying details.
            </p>
          </div>

          {/* Availability Alert Banner */}
          {availability && availability.showBanner && (
            <div className={`mb-6 p-4 rounded-lg border text-xs leading-relaxed ${
              availability.status === 'unavailable' 
                ? 'bg-red-500/10 border-red-500/30 text-red-200' 
                : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-200'
            }`}>
              <span className="font-bold uppercase tracking-wider block mb-1">
                ⚠️ Outreach Notice: {availability.status === 'unavailable' ? 'Service Temporarily Offline' : 'Response Times May Be Delayed'}
              </span>
              <p>{availability.message}</p>
            </div>
          )}

          <p className="text-slate-400 mb-6 text-xs leading-relaxed">
            Help us locate camps in need of assistance. Submissions are only visible to authorized outreach staff. Read our{' '}
            <Link to="/privacy" className="text-banana-yellow hover:underline font-semibold">Privacy Policy</Link>.
          </p>

          {!(availability && availability.acceptCampReports === true) ? (
            <div className="bg-charcoal-card border border-red-500/30 p-6 rounded-lg text-center space-y-4">
              <p className="text-red-200 font-bold uppercase tracking-wider text-xs">
                Camp Reporting is Temporarily Closed
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                We are temporarily unable to accept new camp reports online. Please check back later.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="description" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                Location Description *
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (fieldErrors.description) setFieldErrors((prev) => ({ ...prev, description: null }));
                }}
                rows="3"
                maxLength={2000}
                className={`${getInputClass(!!fieldErrors.description)} resize-none`}
                placeholder="Describe where the camp is located (e.g. Near the railway bridge off Main St, in the wooded area)."
                required
                aria-required="true"
                aria-invalid={!!fieldErrors.description}
                aria-describedby="camp-description-help"
              />
              {fieldErrors.description && (
                <p className="text-xs text-urgent-orange font-bold mt-1" role="alert">
                  ⚠️ {fieldErrors.description}
                </p>
              )}
              <div id="camp-description-help" className="flex justify-between text-[11px] text-slate-500 mt-1">
                <span>Describe what you reasonably observed. Avoid unnecessary names.</span>
                <span aria-live="polite">{description.length}/2000</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                Pin Location on Map
              </label>
              <MapComponent mode="select" onLocationSelect={handleLocationSelect} />
              {latitude != null && longitude != null && (
                <div className="mt-2 text-xs text-hope-green font-medium">
                  📍 Coordinates pinned: {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="peopleCount" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                  Approximate Number of People
                </label>
                <input
                  type="number"
                  id="peopleCount"
                  min="1"
                  max="100"
                  value={peopleCount}
                  onChange={(e) => setPeopleCount(e.target.value)}
                  className={inputClass}
                  placeholder="e.g. 5"
                />
              </div>

              <div>
                <label htmlFor="safetyConcern" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                  Observed Safety Concern?
                </label>
                <select
                  id="safetyConcern"
                  value={safetyConcern ? 'yes' : 'no'}
                  onChange={(e) => setSafetyConcern(e.target.value === 'yes')}
                  className={inputClass}
                >
                  <option value="no">No obvious safety concern</option>
                  <option value="yes">Yes (describe in additional info)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="childrenObserved" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                  Children Observed?
                </label>
                <select
                  id="childrenObserved"
                  value={childrenObserved}
                  onChange={(e) => setChildrenObserved(e.target.value)}
                  className={inputClass}
                >
                  <option value="unknown">Unknown</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              <div>
                <label htmlFor="petsObserved" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                  Pets Observed?
                </label>
                <select
                  id="petsObserved"
                  value={petsObserved}
                  onChange={(e) => setPetsObserved(e.target.value)}
                  className={inputClass}
                >
                  <option value="unknown">Unknown</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="observedNeeds" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                Observed or Requested Supplies
              </label>
              <textarea
                id="observedNeeds"
                value={observedNeeds}
                onChange={(e) => setObservedNeeds(e.target.value)}
                rows="2"
                maxLength={1000}
                className={inputClass}
                placeholder="e.g. Needs heavy blankets, dry food, firewood."
              />
            </div>

            <div>
              <label htmlFor="accessibilityConcerns" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                Accessibility / Mobility Concerns
              </label>
              <input
                type="text"
                id="accessibilityConcerns"
                value={accessibilityConcerns}
                onChange={(e) => setAccessibilityConcerns(e.target.value)}
                maxLength={200}
                className={inputClass}
                placeholder="e.g. Steep incline, difficult terrain to access camp"
              />
            </div>

            <label className="flex items-center space-x-3 cursor-pointer p-3 rounded bg-charcoal-card border border-white/5">
              <input
                type="checkbox"
                checked={spokeWithAnyone}
                onChange={(e) => setSpokeWithAnyone(e.target.checked)}
                className="w-5 h-5 text-accent-cyan border-gray-300 rounded focus:ring-accent-cyan"
              />
              <div>
                <span className="text-sm font-semibold text-slate-200 block">Spoke with anyone at the camp?</span>
                <span className="text-xs text-slate-500">Check if you spoke directly with camp residents to identify their needs.</span>
              </div>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="dateObserved" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                  Date Observed
                </label>
                <input
                  type="date"
                  id="dateObserved"
                  value={dateObserved}
                  onChange={(e) => setDateObserved(e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="timeObserved" className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                  Approximate Time Observed
                </label>
                <input
                  type="time"
                  id="timeObserved"
                  value={timeObserved}
                  onChange={(e) => setTimeObserved(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="border-t border-white/5 pt-6 space-y-6">
              <span className="block text-sm font-bold text-slate-200 uppercase tracking-wider mb-2">
                Your Contact Information (Optional)
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-slate-400 uppercase mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={100}
                    className={inputClass}
                    placeholder="name@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-bold text-slate-400 uppercase mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={30}
                    className={inputClass}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <label className="flex items-center space-x-3 cursor-pointer p-2 rounded hover:bg-white/5">
                <input
                  type="checkbox"
                  checked={permissionToContact}
                  onChange={(e) => setPermissionToContact(e.target.checked)}
                  className="w-5 h-5 text-accent-cyan border-gray-300 rounded focus:ring-accent-cyan"
                />
                <span className="text-xs text-slate-300">You may contact me for additional details or directions to the camp.</span>
              </label>

              <div>
                <label htmlFor="additionalInfo" className="block text-xs font-bold text-slate-400 uppercase mb-2">
                  Additional Information
                </label>
                <textarea
                  id="additionalInfo"
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  rows="3"
                  maxLength={1000}
                  className={inputClass}
                  placeholder="Provide any additional details or instructions here."
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-white/5">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 uppercase text-xs tracking-wide"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 cyber-btn-blue disabled:opacity-50 disabled:cursor-not-allowed uppercase text-xs"
              >
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </form>
          )}
        </div>
      </main>

      <Footer />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ReportCamp;
